import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePublicProcessFaq } from "@/api/queries/processFaq";
import { Accordion, type AccordionItem } from "@/components/ui/Accordion";
import { Tabs, type TabItem } from "@/components/ui/Tabs";

/** FAQ sections for the current public language — shared by the FAQ
 *  accordion and the section-visibility gate so both read the same query. */
function useHomeFaqSections() {
  const { i18n } = useTranslation("common");
  const lang = i18n.language?.startsWith("en") ? "en" : "cs";
  return usePublicProcessFaq(lang);
}

/**
 * True once the tenant has at least one FAQ section to show. Pages use
 * this to gate the whole „Časté dotazy" `<section>` — heading included —
 * so a tenant with FAQ enabled on no process (backend then returns `[]`)
 * gets no empty heading block.
 */
export function useHasHomeFaq(): boolean {
  const { data } = useHomeFaqSections();
  return (data?.length ?? 0) > 0;
}

/**
 * Per-process FAQ section on the homepage and `/jak-funguje`.
 *
 * The list of sections is fetched from `GET /process-faq` which is
 * derived from the tenant's **Mapa výrobních operací** — distinct
 * `process_type`s of publicly-visible `kind=process` nodes in
 * `process_chain_node_layouts`, mapped to their `capability_key`
 * (laser, bending, finishing, 3D print), filtered to operations with
 * the per-tenant `faq_enabled` opt-in. Empty response ⇒ the parent
 * hides the whole section via `useHasHomeFaq` (this component only
 * mounts with data already in the cache, so it renders no skeleton).
 * Tenants with two or more sections see a swatch (Tabs) above the
 * accordion to switch between them; single-section tenants see no
 * swatch — just the accordion.
 *
 * Strings come pre-rendered from the backend (platform defaults +
 * per-tenant overrides on `process_faq_overrides`); the component does
 * not touch i18n for question / answer copy.
 */
export function HomeFaq(): JSX.Element | null {
  const { data } = useHomeFaqSections();
  const [active, setActive] = useState<string | null>(null);

  const current = useMemo(() => {
    if (!data || data.length === 0) return null;
    return (
      data.find((entry) => entry.capability_key === active) ?? data[0] ?? null
    );
  }, [data, active]);

  const items: AccordionItem[] = useMemo(() => {
    if (!current) return [];
    return current.items.map((it, i) => ({
      id: `${current.capability_key}-${i}`,
      title: it.q,
      body: <p>{it.a}</p>,
    }));
  }, [current]);

  if (!current || items.length === 0) {
    // No active modules → nothing to render. Safety net for a caller
    // that didn't gate on `useHasHomeFaq`.
    return null;
  }

  const tabItems: TabItem[] = (data ?? []).map((entry) => ({
    value: entry.capability_key,
    label: entry.display_name,
  }));

  return (
    <div className="space-y-6">
      {tabItems.length > 1 ? (
        <Tabs
          items={tabItems}
          value={current.capability_key}
          onChange={setActive}
          ariaLabel="Vyberte výrobní operaci"
          testId="home-faq-tabs"
        />
      ) : null}
      <Accordion
        items={items}
        marker="plus-minus"
        testId={`home-faq-${current.capability_key}`}
      />
    </div>
  );
}
